/**
 * ============================================================================
 * SICHERHEITS-DOKUMENTATION: Profilbild-Quellen
 * ============================================================================
 *
 * ERLAUBTE Pfade für öffentliche Profilbilder:
 * - /uploads/gallery/        → Öffentliche Character-Gallery
 * - /uploads/avatars/        → Dedizierte Avatar-Uploads
 * - /api/v1/media/gallery/   → Media-Endpoint für Gallery
 * - /api/v1/media/avatars/   → Media-Endpoint für Avatare
 *
 * VERBOTENE Pfade (dürfen NIEMALS als Profilbild erscheinen):
 * - /uploads/chat/           → Chat-generierte Bilder (privat!)
 * - /uploads/creator-private/→ Creator-private Inhalte
 * - /uploads/user-gallery/   → User-spezifische Uploads
 * - /api/v1/media/chat/      → Chat-Media-Endpoint
 * - /private/                → Allgemein private Pfade
 *
 * BACKEND-EMPFEHLUNG:
 * - Das Backend SOLLTE avatar_url automatisch aus dem Gallery-Bild mit
 *   is_profile_image: true setzen
 * - Wenn kein Profilbild in Gallery existiert, sollte avatar_url leer sein
 * - Die Swipe/Match APIs sollten idealerweise die Gallery mit is_profile_image
 *   Informationen liefern
 * - Bei Character-Erstellung MUSS ein Profilbild gesetzt werden
 * ============================================================================
 */

/**
 * VERBOTENE URL-Pfade für Profilbilder
 * Diese Pfade enthalten private oder Chat-spezifische Inhalte
 */
const FORBIDDEN_PROFILE_PATHS = [
  '/chat/',
  '/uploads/chat/',
  '/api/v1/media/chat/',
  '/private/',
  '/creator-private/',
  '/uploads/creator-private/',
  '/user-gallery/',
  '/uploads/user-gallery/',
  '/api/v1/media/user-gallery/',
] as const;

/**
 * ERLAUBTE URL-Pfade für Profilbilder
 */
const ALLOWED_PROFILE_PATHS = [
  '/gallery/',
  '/uploads/gallery/',
  '/api/v1/media/gallery/',
  '/avatars/',
  '/uploads/avatars/',
  '/api/v1/media/avatars/',
] as const;

/**
 * Prüft ob eine URL ein erlaubter Profilbild-Pfad ist.
 *
 * SICHERHEITS-KRITISCH: Diese Funktion verhindert, dass private oder
 * Chat-Bilder als öffentliche Avatare angezeigt werden.
 *
 * @param url - Die zu prüfende URL
 * @returns true wenn die URL ein erlaubter Profilbild-Pfad ist
 *
 * TEST-FÄLLE (sollten implementiert werden):
 * - Test: isValidProfileImageSource('/uploads/chat/123.jpg') → false
 * - Test: isValidProfileImageSource('/uploads/gallery/123.jpg') → true
 * - Test: isValidProfileImageSource('/uploads/creator-private/123.jpg') → false
 * - Test: isValidProfileImageSource('/api/v1/media/avatars/123.jpg') → true
 * - Test: isValidProfileImageSource('') → false
 * - Test: isValidProfileImageSource(undefined) → false
 */
export function isValidProfileImageSource(url: string | undefined | null): boolean {
  if (!url || typeof url !== 'string') {
    return false;
  }

  const normalizedUrl = url.toLowerCase();

  // Prüfe zuerst auf VERBOTENE Pfade (höhere Priorität)
  for (const forbidden of FORBIDDEN_PROFILE_PATHS) {
    if (normalizedUrl.includes(forbidden)) {
      // DEV-Logging für Debugging
      if (import.meta.env.DEV) {
        console.warn(`🚨 [SECURITY] Blocked forbidden profile image path: ${url}`);
      }
      return false;
    }
  }

  // Prüfe auf ERLAUBTE Pfade
  for (const allowed of ALLOWED_PROFILE_PATHS) {
    if (normalizedUrl.includes(allowed)) {
      return true;
    }
  }

  // Externe URLs oder unbekannte Pfade: Strikt ablehnen für Sicherheit
  // (Außer es ist bereits eine vollständige URL die wir nicht analysieren können)
  if (normalizedUrl.startsWith('http://') || normalizedUrl.startsWith('https://')) {
    // Bei externen URLs: Prüfe ob der Pfad-Teil erlaubt ist
    try {
      const parsed = new URL(url);
      const pathname = parsed.pathname.toLowerCase();

      // Prüfe verbotene Pfade
      for (const forbidden of FORBIDDEN_PROFILE_PATHS) {
        if (pathname.includes(forbidden)) {
          if (import.meta.env.DEV) {
            console.warn(`🚨 [SECURITY] Blocked forbidden profile image URL: ${url}`);
          }
          return false;
        }
      }

      // Prüfe erlaubte Pfade
      for (const allowed of ALLOWED_PROFILE_PATHS) {
        if (pathname.includes(allowed)) {
          return true;
        }
      }
    } catch {
      // URL parsing failed - reject for safety
      return false;
    }
  }

  // Unbekannter Pfad: Im Zweifel ablehnen
  if (import.meta.env.DEV) {
    console.warn(`⚠️ [SECURITY] Unknown profile image path rejected: ${url}`);
  }
  return false;
}

export function getImageUrl(url: string | undefined, cacheKey?: string): string {
  if (!url) return '';
  
  // If URL already starts with http, normalize legacy upload paths to media endpoint
  if (url.startsWith('http://') || url.startsWith('https://')) {
    try {
      const u = new URL(url);
      // Match paths like /uploads/gallery/... or /uploads/avatars/...
      if (u.pathname.startsWith('/uploads/gallery/')) {
        const filename = u.pathname.split('/uploads/gallery/')[1];
        u.pathname = `/api/v1/media/gallery/${filename}`;
        return `${u.origin}${u.pathname}`;
      }
      if (u.pathname.startsWith('/uploads/avatars/')) {
        const filename = u.pathname.split('/uploads/avatars/')[1];
        u.pathname = `/api/v1/media/avatars/${filename}`;
        return `${u.origin}${u.pathname}`;
      }
    } catch (_) {
      // fall through
    }
    return url;
  }
  
  // ✅ FIX: Ensure correct backend URL construction for Docker environment
  let backendBaseUrl;
  
  try {
    backendBaseUrl = (import.meta as any).env?.VITE_API_URL?.replace('/api/v1', '');
  } catch (e) {
    // Fallback if import.meta access fails
    backendBaseUrl = null;
  }
  
  if (!backendBaseUrl) {
    // Fallback: Automatische Erkennung für verschiedene Umgebungen
    const { protocol, hostname, port } = window.location;
    
    // Lokale Entwicklung (Docker)
    if (port === '5175') {
      backendBaseUrl = `${protocol}//${hostname}:8002`;
    }
    // Lokale Entwicklung (direkt)
    else if (hostname === 'localhost' || hostname === '127.0.0.1') {
      backendBaseUrl = `${protocol}//${hostname}:8002`;
    }
    // Produktionsumgebung - gleiche Domain
    else {
      backendBaseUrl = `${protocol}//${hostname}`;
    }
  }
  
  // ✅ Map legacy storage paths to protected media endpoints
  // - /uploads/gallery/{filename}      →  /api/v1/media/gallery/{filename}
  // - /uploads/avatars/{filename}      →  /api/v1/media/avatars/{filename}
  // - /uploads/user-gallery/{filename} →  /api/v1/media/user-gallery/{filename}
  let path = url.startsWith('/') ? url : `/${url}`;
  if (path.startsWith('/uploads/gallery/')) {
    const filename = path.split('/uploads/gallery/')[1];
    path = `/api/v1/media/gallery/${filename}`;
  } else if (path.startsWith('/uploads/avatars/')) {
    const filename = path.split('/uploads/avatars/')[1];
    path = `/api/v1/media/avatars/${filename}`;
  } else if (path.startsWith('/uploads/user-gallery/')) {
    const filename = path.split('/uploads/user-gallery/')[1];
    path = `/api/v1/media/user-gallery/${filename}`;
  }
  
  const finalUrl = `${backendBaseUrl}${path}`;
  const withCacheKey = cacheKey
    ? `${finalUrl}${finalUrl.includes('?') ? '&' : '?'}v=${encodeURIComponent(cacheKey)}`
    : finalUrl;
  
  // ❌ REMOVED: Console logging that might cause stack overflow
  // console.log(`🖼️  Image URL: ${url} → ${finalUrl}`);
  
  return withCacheKey;
}

// Generate a unique gradient based on character ID
function getGradientColors(id: number): { color1: string; color2: string } {
  const gradients = [
    { color1: 'FF6B6B', color2: 'FFE66D' }, // Warm sunset
    { color1: 'C06CFF', color2: 'F98CFF' }, // Purple passion
    { color1: 'FF6B9D', color2: 'FFC0CB' }, // Pink romance
    { color1: 'EE7752', color2: 'E73C7E' }, // Fiery red
    { color1: '23A6D5', color2: '23D5AB' }, // Ocean breeze
    { color1: 'F093FB', color2: 'F5576C' }, // Magenta dream
    { color1: 'FA709A', color2: 'FEE140' }, // Summer love
    { color1: 'A890FE', color2: 'FF6CAB' }, // Mystic purple
  ];
  if (gradients.length === 0) {
    return { color1: 'FFFFFF', color2: '000000' };
  }
  const idx = Math.abs(id) % gradients.length;
  return gradients[idx] as { color1: string; color2: string };
}

/**
 * SICHERHEITS-KRITISCH: Gibt ein sicheres Profilbild zurück.
 *
 * Erlaubte Quellen (in Prioritätsreihenfolge):
 * 1. Gallery-Bild mit is_profile_image: true (validiert gegen erlaubte Pfade)
 * 2. Erstes geeignetes Gallery-Bild als Fallback (validiert gegen erlaubte Pfade)
 * 3. avatar_url (nur wenn es NICHT auf verbotene Pfade verweist)
 *
 * VERBOTEN:
 * - Chat-Bilder (/uploads/chat/, /media/chat/)
 * - Private Creator-Bilder (/uploads/creator-private/)
 * - User-Gallery Bilder (/uploads/user-gallery/)
 *
 * HINWEIS: Gallery-Bilder sind grundsätzlich sicher, da sie auf erlaubten Pfaden
 * liegen. Der Fallback auf das erste Gallery-Bild gewährleistet konsistente
 * Anzeige über alle Seiten hinweg (Profil, Matches, Swipe, Chat-Liste).
 *
 * @param character - Das Character-Objekt
 * @param allowNsfw - Ob NSFW-Bilder erlaubt sind (basierend auf Altersverifikation)
 * @returns Die URL des Profilbilds oder '' wenn kein sicheres Profilbild gefunden
 *
 * TEST-FÄLLE (sollten implementiert werden):
 * - Test: Character ohne Gallery und ohne avatar_url → ''
 * - Test: Character mit Chat-Bild in avatar_url → ''
 * - Test: Character mit Gallery ohne is_profile_image → erstes Gallery-Bild
 * - Test: Character mit gültigem is_profile_image → URL
 * - Test: Character mit avatar_url auf erlaubtem Pfad → URL
 * - Test: Gelöschter Character → ''
 */
export function getCharacterAvatarUrl(character: any, allowNsfw = true): string {
  // Gelöschte Characters bekommen kein Profilbild
  if (!character || character?.name?.toLowerCase?.() === 'gelöschter charakter' || character?.name?.toLowerCase?.() === 'deleted character') {
    return '';
  }

  // PRIORITÄT 1: Gallery-Bild mit is_profile_image: true
  if (character?.gallery && Array.isArray(character.gallery)) {
    // Suche NUR nach explizit markierten Profilbildern
    const profileImage = character.gallery.find((img: any) =>
      img.is_profile_image && (allowNsfw || !img.is_fsk18)
    );

    if (profileImage) {
      // Bei Video-Profilbild: Verwende das Thumbnail
      if ((profileImage)?.media_type === 'video' && (profileImage)?.thumbnail_url) {
        const thumbnailUrl = (profileImage).thumbnail_url;
        // SICHERHEITS-CHECK: Validiere auch Thumbnail-URLs
        if (isValidProfileImageSource(thumbnailUrl)) {
          return getImageUrl(thumbnailUrl);
        }
        if (import.meta.env.DEV) {
          console.warn(`🚨 [SECURITY] Invalid thumbnail URL for profile image: ${thumbnailUrl}`);
        }
        return '';
      }

      // SICHERHEITS-CHECK: Validiere die Profilbild-URL
      if (isValidProfileImageSource(profileImage.url)) {
        return getImageUrl(profileImage.url);
      }

      if (import.meta.env.DEV) {
        console.warn(`🚨 [SECURITY] Invalid profile image URL: ${profileImage.url}`);
      }
      return '';
    }

    // PRIORITÄT 1b: Fallback auf erstes geeignetes Gallery-Bild (wenn kein is_profile_image)
    // Gallery-Bilder sind SICHER da sie auf erlaubten Pfaden liegen.
    // Dieser Fallback ist nötig für Characters ohne explizites Profilbild.
    const suitableImages = allowNsfw
      ? character.gallery
      : character.gallery.filter((img: any) => !img.is_fsk18);

    if (suitableImages.length > 0) {
      const firstImage = suitableImages[0];
      
      // Bei Video: Verwende Thumbnail
      if (firstImage?.media_type === 'video' && firstImage?.thumbnail_url) {
        if (isValidProfileImageSource(firstImage.thumbnail_url)) {
          return getImageUrl(firstImage.thumbnail_url);
        }
      }
      
      // SICHERHEITS-CHECK: Validiere auch Fallback-Bilder
      if (firstImage?.url && isValidProfileImageSource(firstImage.url)) {
        return getImageUrl(firstImage.url);
      }
    }
  }

  // PRIORITÄT 2: avatar_url (mit strikter Validierung)
  if (character?.avatar_url && !character.avatar_url.includes('placeholder')) {
    const avatarUrl = String(character.avatar_url);

    // SICHERHEITS-CHECK: Validiere avatar_url gegen verbotene Pfade
    if (!isValidProfileImageSource(avatarUrl)) {
      if (import.meta.env.DEV) {
        console.warn(`🚨 [SECURITY] Character "${character.name || character.id}" has invalid avatar_url: ${avatarUrl}`);
      }
      return '';
    }

    // Video-Check: Kein Video als Profilbild-URL erlauben
    try {
      const lower = avatarUrl.toLowerCase();
      const looksLikeVideo = lower.endsWith('.mp4') || lower.endsWith('.webm') || lower.endsWith('.ogg') || lower.endsWith('.ogv') || lower.endsWith('.mov') || lower.includes('/video/');
      if (looksLikeVideo) {
        // Versuche Thumbnail aus Gallery zu verwenden
        const galleryPoster = Array.isArray(character.gallery)
          ? character.gallery.find((img: any) => img.is_profile_image && (img)?.thumbnail_url)?.thumbnail_url
          : undefined;
        if (galleryPoster && isValidProfileImageSource(galleryPoster)) {
          return getImageUrl(galleryPoster);
        }
        // Kein gültiges Poster verfügbar → Placeholder
        return '';
      }
    } catch {
      // ignore detection errors
    }

    return getImageUrl(avatarUrl);
  }

  // Kein sicheres Profilbild gefunden → Placeholder wird angezeigt
  return '';
}

/**
 * Prüft ob für einen Character ein gültiges Profilbild angezeigt werden kann.
 *
 * SICHERHEITS-KRITISCH: Verwendet die gleiche strikte Logik wie getCharacterAvatarUrl()
 */
export function canDisplayCharacterImage(character: any): boolean {
  if (!character) return false;
  const normalized = character?.name?.toLowerCase?.() || '';
  if (normalized === 'gelöschter charakter' || normalized === 'deleted character') {
    return false;
  }

  // Prüfe auf gültiges Bild in Gallery (Profilbild oder Fallback)
  if (character?.gallery && Array.isArray(character.gallery)) {
    // Priorität 1: is_profile_image
    const profileImage = character.gallery.find((img: any) => img.is_profile_image);
    if (profileImage) {
      const urlToCheck = (profileImage)?.media_type === 'video'
        ? (profileImage)?.thumbnail_url
        : profileImage.url;
      if (isValidProfileImageSource(urlToCheck)) {
        return true;
      }
    }
    
    // Priorität 2: Erstes Gallery-Bild als Fallback (ist sicher da erlaubter Pfad)
    if (character.gallery.length > 0) {
      const firstImage = character.gallery[0];
      const urlToCheck = firstImage?.media_type === 'video'
        ? firstImage?.thumbnail_url
        : firstImage?.url;
      if (isValidProfileImageSource(urlToCheck)) {
        return true;
      }
    }
  }

  // Prüfe avatar_url mit Validierung
  if (character?.avatar_url && !character.avatar_url.includes('placeholder')) {
    if (isValidProfileImageSource(character.avatar_url)) {
      return true;
    }
  }

  return false;
}

/**
 * SICHERHEITS-KRITISCH: Gibt nur das offizielle Creator-Profilbild zurück.
 * Verwendet die gleiche strikte Sicherheitslogik wie getCharacterAvatarUrl().
 */
export function getCreatorAvatarUrl(creator: any, allowNsfw = true): string {
  // Delegiere an getCharacterAvatarUrl für konsistente Sicherheit
  if (creator?.character) {
    return getCharacterAvatarUrl(creator.character, allowNsfw);
  }
  return '';
}